'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { SearchIcon, PackageSearchIcon, LoaderIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { validateOrderId } from '@/lib/api';
import { useAppLocale } from '@/hooks';
const ULID_PATTERN = /^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]{26}$/;
export default function OrderTrackSearchForm() {
  const t = useTranslations();
  const router = useRouter();
  const {
    locale
  } = useAppLocale();
  const [trackingId, setTrackingId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const normalized = trackingId.trim().toUpperCase();
  const isValidFormat = ULID_PATTERN.test(normalized);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!normalized || !isValidFormat) {
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await validateOrderId(normalized);
      router.push(`/${locale}/order-track/${result.id}`);
    } catch {
      setError(t('order_not_found_desc'));
    } finally {
      setIsLoading(false);
    }
  };
  const showFormatError = normalized.length > 0 && !isValidFormat;
  return <div className="container mx-auto max-w-lg px-4 py-16">
      <div className="mb-8 flex flex-col items-center gap-6 text-center">
        <div className="bg-primary/10 flex h-20 w-20 items-center justify-center rounded-full">
          <PackageSearchIcon className="text-primary h-10 w-10" />
        </div>
        <div>
          <h1 className="text-3xl font-bold sm:text-4xl">{t('track_order_search_title')}</h1>
          <p className="text-muted-foreground mt-2 text-base">{t('track_order_search_desc')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <Label htmlFor="tracking-id" className="text-base">
            {t('tracking_id')}
          </Label>
          <Input id="tracking-id" value={trackingId} onChange={e_0 => {
          setTrackingId(e_0.target.value.toUpperCase());
          setError(null);
        }} placeholder={t('tracking_id_placeholder')} className="h-12 font-mono text-base uppercase" autoComplete="off" autoFocus maxLength={26} />
          {showFormatError && <p className="text-destructive text-sm">
              {t('tracking_id')}: {normalized.length}/26
            </p>}
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>

        <Button type="submit" disabled={!isValidFormat || isLoading} className="h-12 w-full gap-2 text-base uppercase">
          {isLoading ? <LoaderIcon className="h-5 w-5 animate-spin" /> : <SearchIcon className="h-5 w-5" />}
          {t('track_order')}
        </Button>
      </form>
    </div>;
}